import {calculateTime, downloadActionFile, EMPTY_VALUES} from '../../../../../Apis/functions';
import ButtonAction from '../../../../../components/buttons/ButtonAction';
import { CommentsTitle, CompletePersonChip, FileTracking } from '../../../../../components/buttons/IconButtons';
import CustomCard from '../../../../../components/CustomCard';
import CustomFileCard from '../../../../../components/CustomFileCard';
import CustomTextFile from '../../../../../components/CustomTextFile';
import CustomTimeLineItems from '../../../../../components/CustomTimeLineItems';
import CustomTooltip from '../../../../../components/CustomTooltip';
import NotInformation from '../../../../../components/NotInformation';
import { getFileNameWithoutExtension } from '../../../../../Helpers/fileType';
import { FILE_MODEL } from '../../../Visitor/Visitor/Model/Model';
/**
 * @Email       : sgarcia@ibl.mx
 * @Author      : Susana Garcia Rodriguez
 * @JobTitle    : Frontend
 *
 * Component that displays the Activity Tracker card and allows marking
 * the activity as completed.
 *
 * @param {Object}   item                  Data of the current activity item displayed in the card.
 * @param {Function} t                     Translation function (i18n) to render text in the selected language.
 * @param {Function} handleMarkAsCompleted Callback executed when the user clicks the "Mark as Completed" button.
 * @param {Function} setCommentData        Function used to update the comment-related state of the activity.
 * @param {Boolean}  showCompletedButton   Determines whether the "Mark as Completed" button should be displayed. Default: true.
 * @param {Boolean}  showCommentBox        Determines whether the comment box should be displayed. Default: true.
 */
const ActivityTrackerCard = ({
    item,
    t,
    handleMarkAsCompleted,
    showCompletedButton = true,
    showCommentBox = true,
    comments,
    handleSendMessage,
    emptyFields,
    modified,
    setModified
}) => {
    const createdAtText = item?.createdAt ? calculateTime(item?.createdAt) : EMPTY_VALUES.DEFAULT;
    const closedDateText = calculateTime(item?.closedDate);
    const showNotInformation = !item || (!item?.deliveryByFullName && !item?.closedById);

    return (
        <CustomCard classCard='card-border-8px' classContent='p-0'>
            <div className='d-flex align-items-center m-3'>
                <div className='d-flex align-items-center'>
                    <FileTracking className='h-24px w-24px' />
                    <p className='fs-14px font-color-general-950 ibl-font-w-600 ml-2'>{t('activity_tracker')}</p>
                </div>
                {!item?.closedById && item?.deliveryByFullName && showCompletedButton && (
                    <ButtonAction
                        id='btn_atc_mark_as_completed'
                        name='mark_as_completed'
                        className='btn-outlined-secondary-600-00 h-40px w-220px p-0 ml-auto'
                        onClick={handleMarkAsCompleted}
                        splitName={false}
                    />
                )}
            </div>
            <div className='border-bottom-shades-300' />
            <div className={`pt-3 ${comments?.length > 0 ? 'maxh-320px' : ''} overflow-y-auto`}>
                {showNotInformation && <NotInformation text={t('no_activity_tracker')} card={true} />}
                {item?.deliveryByFullName && (
                    <CustomTimeLineItems isFirst={true} isLast={false} className='timeline-item timeline-item-first'>
                        <div className='px-3 pt-1'>
                            <div className={'d-flex flex-grow-1 minw-0'}>
                                <div className={'mr-2 d-flex align-items-center'}>
                                    <CompletePersonChip />
                                </div>

                                <div className={'minw-0'}>
                                    <CustomTooltip
                                        text={item?.deliveryByFullName}
                                        classes='fs-14px font-color-general-950 ibl-font-w-600 ml-2 text-ellipsis-1-row text-capitalize'
                                        showIfTruncated={true}
                                        placement='top'
                                    />
                                </div>

                                <span className='fs-14px font-color-general-950 ibl-font-w-400 ml-2 flex-shrink-0 text-nowrap'>
                                    {t('created_the_required').toLowerCase()}.{' '}
                                </span>
                                <span className='fs-12px font-color-shades-500 ibl-font-w-400 ml-2 d-flex align-items-center flex-shrink-0 text-nowrap'>
                                    {createdAtText === t('just_now')
                                        ? createdAtText
                                        : t('X_MIN_ago', { min: createdAtText }).toLowerCase()}
                                </span>
                            </div>
                        </div>
                    </CustomTimeLineItems>
                )}

                {comments?.map((comment, index) => {
                    const isLastComment = index === comments.length - 1 && !item?.closedById;
                    const file = comment.fileUploadDto ? FILE_MODEL.set(comment) : null;
                    const createdAtText = calculateTime(comment?.createdAt);
                    return (
                        <CustomTimeLineItems
                            key={comment.id}
                            isFirst={false}
                            isLast={isLastComment}
                            className='timeline-item'
                        >
                            <CustomCard
                                classes='p-3'
                                classContent='background-shades-50'
                                classCard='background-shades-50'
                            >
                                <div className='mb-3 background-shades-50 d-flex align-items-center flex-nowrap'>
                                    <div className='flex-shrink-0'> <CommentsTitle /></div>
                                    <div className='minw-0'>
                                        <CustomTooltip
                                            text={comment.userFullName}
                                            classes='fs-14px font-color-general-950 ibl-font-w-600 ml-2 text-ellipsis-1-row'
                                            showIfTruncated={true}
                                        />
                                    </div>
                                    <span className='fs-12px font-color-shades-500 ibl-font-w-400 ml-2 flex-shrink-0 text-nowrap'>
                                        {createdAtText === t('just_now')
                                            ? t('just_now')
                                            : t('X_MIN_ago', { min: createdAtText }).toLowerCase()}
                                    </span>
                                </div>

                                <CustomCard classes='mt-3'>
                                    <span className='fs-14px font-color-general-950 ibl-font-w-300 text-wrap'>
                                        {comment.message}
                                    </span>

                                    {file && (
                                        <div className='mt-3'>
                                            <CustomFileCard
                                                Icon={file.Icon}
                                                title={getFileNameWithoutExtension(file.name)}
                                                subtitle={file.size}
                                                fileValue={file}
                                                buttonId={'btn_atc_download_' + comment.id}
                                                onClick={() => downloadActionFile(file.filePath)}
                                                showIcon
                                            />
                                        </div>
                                    )}
                                </CustomCard>
                            </CustomCard>
                        </CustomTimeLineItems>
                    );
                })}

                {item?.closedById && (
                    <CustomTimeLineItems isLast={true} className='timeline-item timeline-item-last'>
                        <div className='px-3 pt-1'>
                            <div className={'d-flex flex-grow-1 minw-0'}>
                                <div className={'mr-2 d-flex align-items-center'}>
                                    <CompletePersonChip />
                                </div>
                                <div className={'minw-0'}>
                                    <CustomTooltip
                                        text={item?.closedByFullName}
                                        classes='fs-14px font-color-general-950 ibl-font-w-600 ml-2 text-ellipsis-1-row text-capitalize'
                                        showIfTruncated={true}
                                        placement='top'
                                    />
                                </div>
                                <span className='fs-14px font-color-general-950 ibl-font-w-400 ml-2 flex-shrink-0 text-nowrap'>
                                    {t('marked_the_required_as_closed').toLowerCase()}.
                                </span>
                                <span className='fs-12px font-color-shades-500 ibl-font-w-400 ml-2 d-flex align-items-center flex-shrink-0 text-nowrap'>
                                    {closedDateText === t('just_now')
                                        ? t('just_now')
                                        : t('X_MIN_ago', { min: closedDateText }).toLowerCase()}
                                </span>
                            </div>
                        </div>
                    </CustomTimeLineItems>
                )}
            </div>
            <div className='m-3'>
                <div className='m-3'>
                    {!item?.closedById && item?.deliveryByFullName && showCommentBox && (
                        <CustomTextFile
                            id='txt_atc_comment'
                            onSend={handleSendMessage}
                            required={true}
                            emptyFields={emptyFields}
                            emptyFieldsText={'comment_is_required'}
                            showCounter={true}
                            length={512}
                            clear={modified}
                            setClear={setModified}
                        />
                    )}
                </div>
            </div>
        </CustomCard>
    );
};

export default ActivityTrackerCard;
